import { useMemo, useState } from 'react';
import type { OpenTarget } from '../components/Matrix/types.js';
import { useConfirmedAdopt } from '../components/Adopt/AdoptConfirm.js';
import { usePull } from './usePull.js';
import { usePush } from './usePush.js';
import { useSelection } from './useSelection.js';

/**
 * The hooks the Studios and Games pages both wire up the same way: selection, which entity
 * drawer is open, which environment's inbox is open, pulling, the push modal's controller and
 * adopt. A page calls this once and hands the result to `<MatrixOverlays>`, which renders what
 * they drive; the page itself only decides what selects a row and what a Pull now button targets.
 *
 * The returned object is memoised on its own stable parts (`selection` is itself memoised;
 * `setDrawer`/`setInbox` are `useState` setters, always stable). `pulls`/`push`/`adoption` are
 * NOT yet stabilised — `usePull`/`usePush`/`useConfirmedAdopt` return a fresh object every
 * render — so a caller that memoises something on this hook's return value as a whole still
 * recomputes every render; depend on `matrix.selection` (or a narrower slice) rather than
 * `matrix` itself where that matters.
 */
export const useMatrixPageState = () => {
    const selection = useSelection();
    const [drawer, setDrawer] = useState<OpenTarget | null>(null);
    const [inbox, setInbox] = useState<string | null>(null);
    const pulls = usePull();
    const push = usePush();
    const adoption = useConfirmedAdopt();

    return useMemo(
        () => ({ selection, drawer, setDrawer, inbox, setInbox, pulls, push, adoption }),
        [selection, drawer, setDrawer, inbox, setInbox, pulls, push, adoption],
    );
};

export type MatrixPageState = ReturnType<typeof useMatrixPageState>;
